import { useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Fuel } from 'lucide-react';
import { LogEntry, Location, Car } from '../lib/supabase';
import { useTranslations } from '../i18n';
import { formatCurrency } from '../lib/conversions';
import { useOutletContext } from 'react-router-dom';
import defaultExpenseTypesData from '../data/defaultExpenseTypes.json';
import { LocationItem } from '../components/cars/LocationItem';

// Fix leaflet icon issue (reused from LocationForm.tsx)
if (typeof window !== 'undefined') {
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    });
}

const getMarkerIcon = (color: string) => {
    return L.divIcon({
        html: `
            <svg viewBox="0 0 24 24" width="32" height="32" stroke="white" stroke-width="2" fill="${color}" stroke-linecap="round" stroke-linejoin="round" style="filter: drop-shadow(0px 2px 4px rgba(0,0,0,0.3));">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3" fill="white" stroke="none"></circle>
            </svg>
        `,
        className: 'custom-map-marker',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
    });
};

interface ExpensesMapViewProps {
    logEntries: LogEntry[];
    car: Car;
    isPublicView: boolean;
}

export function ExpensesMapView({ logEntries, car, isPublicView }: ExpensesMapViewProps) {
    const { t, language } = useTranslations();
    const { ownerPreferences, isAdminView } = useOutletContext<any>() || {};
    const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

    const selectedYear = useMemo(() => {
        const saved = sessionStorage.getItem('stats_selectedYear');
        if (saved === 'all') return 'all';
        if (saved) return parseInt(saved);
        return 'all';
    }, []);

    const currency = ownerPreferences?.currency || 'EUR';
    const currencySymbol = ownerPreferences?.currency_symbol || '€';
    const volumeUnit = ownerPreferences?.volume_unit || 'liters';

    const locationStats = useMemo(() => {
        const stats: Record<string, {
            location: Location;
            totalAmount: number;
            totalLiters: number;
            visitCount: number;
            lastVisit: string;
            lastExpenseType?: string | null;
        }> = {};

        // If public view and sharing is disabled (and not explicit admin view), show nothing
        if (isPublicView && !car.share_locations && !isAdminView) {
            return [];
        }

        const filteredEntries = selectedYear === 'all'
            ? logEntries
            : logEntries.filter(e => new Date(e.date).getFullYear() === selectedYear);

        filteredEntries.forEach(entry => {
            // Be very robust about finding the location object
            const rawLoc = (entry as any).location || (entry as any).locations;
            // PostgREST may return array if joined incorrectly, check both
            let loc = Array.isArray(rawLoc) ? rawLoc[0] : rawLoc;

            // If loc is MISSING, skip
            if (!loc) return;

            // Coordinate parsing:parseFloat works for both strings and numbers
            const lat = parseFloat(loc.latitude?.toString() || '');
            const lng = parseFloat(loc.longitude?.toString() || '');

            // Ensure coordinates are valid
            if (!isNaN(lat) && !isNaN(lng)) {
                if (!stats[loc.id]) {
                    stats[loc.id] = {
                        location: { ...loc, latitude: lat, longitude: lng },
                        totalAmount: 0,
                        totalLiters: 0,
                        visitCount: 0,
                        lastVisit: entry.date,
                        lastExpenseType: entry.expenses?.[0]?.type || null
                    };
                }

                // Sum up expenses in this log entry
                const entryTotal = (entry.expenses || []).reduce((sum: number, exp: any) => sum + (exp.amount ? parseFloat(exp.amount.toString()) : 0), 0);
                const entryLiters = (entry.expenses || []).reduce((sum: number, exp: any) => sum + (exp.fuel_liters || 0), 0);

                stats[loc.id].totalAmount += entryTotal;
                stats[loc.id].totalLiters += entryLiters;
                stats[loc.id].visitCount += 1;

                // Track last visit date accurately
                if (!stats[loc.id].lastVisit || new Date(entry.date) > new Date(stats[loc.id].lastVisit)) {
                    stats[loc.id].lastVisit = entry.date;
                    stats[loc.id].lastExpenseType = entry.expenses?.[0]?.type || null;
                }
            }
        });

        // Sort locations by total spending (descending)
        return Object.values(stats).sort((a, b) => b.totalAmount - a.totalAmount);
    }, [logEntries, selectedYear]);

    const mapCenter: [number, number] = locationStats.length > 0
        ? [locationStats[0].location.latitude!, locationStats[0].location.longitude!]
        : [56.9496, 24.1052]; // Default Riga

    return (
        <div className="flex flex-col h-full bg-transparent font-primary">
            {/* Map Section */}
            <div className="flex-1 min-h-[40vh] relative">
                <div className="absolute inset-0 overflow-hidden">
                    <div className="h-full w-full max-w-2xl mx-auto relative shadow-xl overflow-hidden rounded-b-3xl">
                        <MapContainer center={mapCenter} zoom={12} className="h-full w-full z-10 transition-all duration-1000">
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                            {locationStats.map(stat => {
                                const typeInfo = stat.lastExpenseType ? (defaultExpenseTypesData as any)[stat.lastExpenseType] : null;
                                const iconColor = typeInfo?.color || '#10b981'; // Default emerald-500

                                return (
                                    <Marker
                                        key={stat.location.id}
                                        position={[stat.location.latitude!, stat.location.longitude!]}
                                        icon={getMarkerIcon(iconColor)}
                                        eventHandlers={{
                                            click: () => setSelectedLocationId(stat.location.id)
                                        }}
                                    >
                                        <Popup className="custom-popup">
                                            <div className="p-1 min-w-[140px]">
                                                <h3 className="font-bold text-gray-900 mb-1 leading-tight">{stat.location.name}</h3>
                                                <div className="space-y-1">
                                                    <p className="text-blue-600 font-black text-sm">
                                                        {formatCurrency(stat.totalAmount, currency as any, currencySymbol)}
                                                    </p>
                                                    {stat.totalLiters > 0 && (
                                                        <p className="text-[11px] text-gray-500 flex items-center gap-1">
                                                            <Fuel size={10} className="text-blue-400" /> {stat.totalLiters.toFixed(1)} {volumeUnit === 'gallons' ? 'gal' : 'L'}
                                                        </p>
                                                    )}
                                                    <div className="flex items-center justify-between gap-2 pt-1 border-t border-gray-100 mt-1">
                                                        <span className="text-[10px] text-gray-400">
                                                            {stat.visitCount} {(t.statistics as any).visitsCountLabel || (language === 'lv' ? 'vizītes' : 'visits')}
                                                        </span>
                                                        <span className="text-[10px] text-gray-400">
                                                            {new Date(stat.lastVisit).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Popup>
                                    </Marker>
                                );
                            })}
                        </MapContainer>
                    </div>
                </div>
            </div>

            {/* List Section */}
            <div className="flex-1 bg-transparent overflow-y-auto no-scrollbar z-20 pb-20">
                <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
                    {locationStats.length > 0 && (
                        <div className="flex items-center justify-between px-1 mb-2">
                            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest opacity-80">
                                {(t.statistics as any).locationsSummary || (language === 'lv' ? 'Lokāciju kopsavilkums' : 'Locations Summary')}
                            </p>
                            <p className="text-[11px] font-bold text-gray-400 opacity-80">
                                {locationStats.length} {(t.statistics as any).locationsCountLabel || (language === 'lv' ? 'vietas' : 'places')}
                            </p>
                        </div>
                    )}
                    <div className="grid gap-2 mb-8">
                        {locationStats.map(stat => (
                            <LocationItem
                                key={stat.location.id}
                                location={{
                                    ...stat.location,
                                    last_expense_type: stat.lastExpenseType || null,
                                    entryCount: stat.visitCount
                                } as any}
                                selected={selectedLocationId === stat.location.id}
                                onClick={() => setSelectedLocationId(stat.location.id)}
                                className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm hover:bg-white/90 dark:hover:bg-slate-800/90"
                                suffix={(
                                    <div className="text-right pl-4 flex flex-col justify-center">
                                        <p className="font-black text-[15px] text-gray-900 dark:text-white whitespace-nowrap">
                                            {formatCurrency(stat.totalAmount, currency as any, currencySymbol)}
                                        </p>
                                        <div className="flex items-center justify-end gap-2 mt-0.5">
                                            {stat.totalLiters > 0 && (
                                                <span className="text-[11px] text-blue-500 font-bold whitespace-nowrap">
                                                    {stat.totalLiters.toFixed(1)} {volumeUnit === 'gallons' ? 'gal' : 'L'}
                                                </span>
                                            )}
                                            <span className="text-[11px] text-gray-400 whitespace-nowrap">
                                                {stat.visitCount}x
                                            </span>
                                        </div>
                                    </div>
                                )}
                            />
                        ))}
                        {locationStats.length === 0 && (
                            <div className="py-12 text-center bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm rounded-3xl border border-dashed border-gray-200 dark:border-white/10">
                                <div className="w-12 h-12 bg-gray-50/50 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <MapPin className="text-gray-400/50" size={24} />
                                </div>
                                <p className="text-[15px] font-bold text-gray-900 dark:text-white mb-1">
                                    {(t.statistics as any).noLocationsFoundTitle || (language === 'lv' ? 'Vēl nav lokāciju' : 'No locations yet')}
                                </p>
                                <p className="text-sm text-gray-400">
                                    {(t.statistics as any).noLocationsFoundDesc || (language === 'lv' ? 'Pievieno lokācijas saviem ierakstiem, lai tās redzētu šeit.' : 'Add locations to your records to see them on the map.')}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
